// src/components/Combobox.tsx
//
// Generic typeahead/combobox primitive built on a plain controlled <input> +
// @radix-ui/react-popover (portal/positioning) with our own filtering and
// keyboard navigation. Previously built on cmdk, but cmdk 0.2.1's controlled
// Command.Input dropped the controlled value at runtime for certain instances
// (store desync) — showing the placeholder despite `query` holding the real
// value — so cmdk was removed entirely. We own the displayed text (value={query})
// and the highlighted-option state directly.
// First caller is JobLineItemsEditor (line items library typeahead); future
// callers can replace the native <select>s in NewJobModal.
//
// Prop contract:
//   value          — currently selected item (T | null)
//   onChange       — fires when user picks an item from the dropdown (NOT on
//                    freeform typing — freeform updates flow via onInputChange)
//   options        — full unfiltered list; filtering happens inside the component
//   getKey         — stable id (used as React key on each option)
//   getLabel       — string used both for filter matching and the default input
//                    display when uncontrolled
//   renderOption   — controls the option row markup (left label, right meta, etc)
//
//   inputValue     — controlled input text (use with onInputChange). When both
//                    are passed, the parent owns the input string.
//   onInputChange  — fires on every keystroke with the new input string.
//   placeholder    — input placeholder text
//   disabled       — read-only display, no interaction
//   emptyMessage   — text shown inside the dropdown when filter yields nothing
//                    (default "No matches")
//   allowFreeform  — default false. When true, typing text that matches nothing
//                    is still a valid value: the input keeps its content on
//                    Escape/blur, and onChange is NOT called with a stale option.
//   onBlur         — fires when the input loses focus (outside-click, Escape, or
//                    selection). Lets callers commit freeform text on blur
//                    instead of on every keystroke.
//   autoFocus      — focuses the input on mount; used by callers that need to
//                    drop focus into a freshly added row.
//
// Behavior:
//   - Popover opens on input focus, closes on Escape, outside click, or
//     selection.
//   - Filter is case-insensitive substring match on getLabel(option).
//   - Keyboard: ArrowDown/Up navigates, Enter selects, Escape closes.
//   - Dark theme; mirrors the bordered-on-focus styling of CellTextInput in
//     JobLineItemsEditor.
//   - Popover width matches the input width (--radix-popover-trigger-width).
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'

type Props<T> = {
  value: T | null
  onChange: (item: T | null) => void
  options: T[]
  getKey: (item: T) => string
  getLabel: (item: T) => string
  renderOption: (item: T) => React.ReactNode
  inputValue?: string
  onInputChange?: (query: string) => void
  placeholder?: string
  disabled?: boolean
  emptyMessage?: string
  allowFreeform?: boolean
  onBlur?: () => void
  autoFocus?: boolean
}

export default function Combobox<T>({
  value, onChange, options, getKey, getLabel, renderOption,
  inputValue, onInputChange,
  placeholder, disabled = false, emptyMessage = 'No matches', allowFreeform = false,
  onBlur, autoFocus = false,
}: Props<T>) {
  const controlled = inputValue !== undefined
  const initial = controlled ? inputValue! : (value ? getLabel(value) : '')
  const [query, setQuery] = useState<string>(initial)
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync local query when parent-controlled inputValue changes.
  useEffect(() => {
    if (controlled) setQuery(inputValue!)
  }, [controlled, inputValue])

  // When value changes externally and the input is uncontrolled, mirror the
  // new label into the input box.
  useEffect(() => {
    if (controlled) return
    setQuery(value ? getLabel(value) : '')
  }, [controlled, value, getLabel])

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => getLabel(o).toLowerCase().includes(q))
  }, [options, query, getLabel])

  // Reset the keyboard highlight when the popover closes. (Typing resets it in
  // handleInputChange — we deliberately do NOT depend on `filtered` here: it's a
  // fresh reference every render when consumers pass getLabel inline, which would
  // reset the highlight mid-arrow-nav.)
  useEffect(() => { if (!open) setHighlightedIndex(-1) }, [open])

  const handleInputChange = (next: string) => {
    setQuery(next)
    onInputChange?.(next)
    setHighlightedIndex(-1) // typing invalidates the previous highlight
    if (!open) setOpen(true)
  }

  const handleSelect = (item: T) => {
    const label = getLabel(item)
    setQuery(label)
    onInputChange?.(label)
    onChange(item)
    setOpen(false)
    inputRef.current?.blur()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) setOpen(true)
      setHighlightedIndex(i => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
        handleSelect(filtered[highlightedIndex])
      } else if (allowFreeform) {
        // No highlighted option — commit the typed text via blur.
        setOpen(false)
        inputRef.current?.blur()
      }
    } else if (e.key === 'Escape') {
      if (!allowFreeform) {
        const reverted = value ? getLabel(value) : ''
        setQuery(reverted)
        onInputChange?.(reverted)
      }
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  if (disabled) {
    return (
      <span style={{ fontSize: 13, color: query ? 'var(--ink-primary)' : 'var(--ink-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {query || placeholder}
      </span>
    )
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div style={{ width: '100%' }}>
          <input
            ref={inputRef}
            value={query}
            onChange={e => handleInputChange(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            role="combobox"
            aria-expanded={open}
            aria-activedescendant={highlightedIndex >= 0 ? `cb-opt-${highlightedIndex}` : undefined}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: open ? 'var(--hover-bg)' : 'transparent',
              border: '1px solid', borderColor: open ? 'var(--accent-border)' : 'transparent',
              borderRadius: 6, padding: '5px 8px', fontSize: 13, color: 'var(--ink-primary)', outline: 'none',
            }}
          />
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={e => e.preventDefault()}
          onCloseAutoFocus={e => e.preventDefault()}
          role="listbox"
          style={{
            width: 'var(--radix-popover-trigger-width)',
            maxHeight: 260, overflow: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-popover-mid)',
            zIndex: 300,
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
              {emptyMessage}
            </div>
          ) : filtered.map((opt, i) => (
            <div
              key={getKey(opt)}
              id={`cb-opt-${i}`}
              role="option"
              aria-selected={i === highlightedIndex}
              onMouseEnter={() => setHighlightedIndex(i)}
              // mousedown (not click) so selection fires before the input's blur
              onMouseDown={e => { e.preventDefault(); handleSelect(opt) }}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--ink-primary)',
                background: i === highlightedIndex ? 'var(--hover-bg)' : 'transparent',
              }}
            >
              {renderOption(opt)}
            </div>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
